import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';
import { MarkdownHtmlPipe } from '../shared/markdown-html.pipe';
import { LocalDatePipe } from '../shared/local-date.pipe';
import { ContentService, LegalPolicy } from '../widgets/content/content.service';
import { SiteConfigService } from '../core/site-config.service';

@Component({
  selector: 'app-legal-page',
  imports: [LocalDatePipe, MarkdownHtmlPipe, RouterLink],
  template: `
    <section class="legal-hero">
      <p class="eyebrow">{{ i18n.t('legal') }}</p>
      <h1>{{ policy()?.title || copy().title }}</h1>
      <p class="lead">
        {{ policy()?.shortDescription || copy().lead }}
      </p>
      @if (policy()) {
        <a class="pill-link" [routerLink]="i18n.routePath('legal')">{{ copy().allPages }}</a>
      }
    </section>

    <section class="legal-shell">
      @if (!slug()) {
        @if (policies().length) {
          <div class="legal-grid">
            @for (item of policies(); track item.id) {
              <a class="legal-card" [routerLink]="i18n.routePath('legalDetail', { slug: item.slug })">
                <span>{{ item.policyType || 'policy' }}</span>
                <h2>{{ item.title }}</h2>
                <p>{{ item.shortDescription || copy().openPolicy }}</p>
              </a>
            }
          </div>
        } @else if (fallbackPolicies().length) {
          <div class="legal-grid">
            @for (item of fallbackPolicies(); track item.title) {
              <article class="legal-card">
                <span>{{ item.type }}</span>
                <h2>{{ item.title }}</h2>
                <p>{{ item.content }}</p>
              </article>
            }
          </div>
        } @else {
          <article class="empty-card">
            <h2>{{ copy().noPolicies }}</h2>
            <p>{{ copy().noPoliciesLead }}</p>
          </article>
        }
      } @else {
        <article class="legal-article">
          @if (policy()) {
            <div class="markdown" [innerHTML]="(policy()?.content || '') | markdownHtml"></div>
            @if (policy()?.updatedAt) {
              <p class="updated">{{ copy().updated }}: {{ policy()?.updatedAt | localDate:'date' }}</p>
            }
          } @else {
            <h2>{{ copy().notFound }}</h2>
            <p>{{ copy().notFoundLead }}</p>
          }
        </article>
      }
    </section>
  `,
  styles: [`
    .legal-hero,
    .legal-shell {
      margin: 0 auto;
      max-width: 1180px;
    }

    .legal-hero {
      background:
        radial-gradient(circle at 80% 0%, var(--glow), transparent 34%),
        linear-gradient(135deg, var(--surface), color-mix(in srgb, var(--accent) 8%, var(--surface)));
      border: 1px solid var(--border);
      border-radius: 42px;
      box-shadow: 0 30px 80px var(--shadow);
      padding: clamp(34px, 7vw, 78px);
    }

    .legal-shell {
      margin-top: 22px;
    }

    .legal-grid {
      display: grid;
      gap: 16px;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .legal-card,
    .legal-article,
    .empty-card {
      background:
        radial-gradient(circle at 20% 0%, color-mix(in srgb, var(--accent) 18%, transparent), transparent 14rem),
        var(--card);
      border: 1px solid var(--border);
      border-radius: 30px;
      box-shadow: 0 20px 58px color-mix(in srgb, var(--shadow) 70%, transparent);
    }

    .legal-card {
      color: var(--ink);
      display: grid;
      gap: 12px;
      min-height: 190px;
      padding: 24px;
      text-decoration: none;
    }

    .legal-card span,
    .eyebrow {
      color: var(--accent);
      font-size: 12px;
      font-weight: 950;
      letter-spacing: .14em;
      text-transform: uppercase;
    }

    .legal-card h2,
    .legal-article h2,
    .empty-card h2 {
      color: var(--ink);
      letter-spacing: -.04em;
      margin: 0;
    }

    .legal-card p,
    .lead,
    .empty-card p,
    .updated {
      color: var(--muted);
      line-height: 1.6;
    }

    .pill-link {
      background: var(--ink);
      border-radius: 999px;
      color: var(--paper);
      display: inline-flex;
      font-weight: 950;
      margin-top: 20px;
      padding: 12px 16px;
      text-decoration: none;
    }

    .legal-article,
    .empty-card {
      padding: clamp(24px, 5vw, 54px);
    }

    .markdown {
      color: var(--muted);
      font-size: 18px;
      line-height: 1.75;
    }

    .markdown ::ng-deep h1,
    .markdown ::ng-deep h2,
    .markdown ::ng-deep h3 {
      color: var(--ink);
      letter-spacing: -.04em;
      line-height: 1.05;
      margin-top: 32px;
    }

    .updated {
      border-top: 1px solid var(--border);
      margin-top: 28px;
      padding-top: 18px;
    }

    @media (max-width: 900px) {
      .legal-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class LegalPage {
  private readonly route = inject(ActivatedRoute);
  protected readonly i18n = inject(I18nService);
  private readonly content = inject(ContentService);
  private readonly seo = inject(SeoService);
  private readonly site = inject(SiteConfigService);
  protected readonly slug = signal('');
  protected readonly policies = signal<LegalPolicy[]>([]);
  protected readonly policy = signal<LegalPolicy | null>(null);
  protected readonly fallbackPolicies = computed(() => {
    if (this.site.config().brandKey !== 'bdsmbeds') return [];
    const rows: Record<string, Array<{type:string;title:string;content:string}>> = {
      en: [
        {type:'sales',title:'Terms of sale',content:'Product specifications, the final price, delivery method and payment details are presented before an order is confirmed.'},
        {type:'delivery',title:'Shipping and delivery',content:'Available destinations, delivery estimates and charges are shown at checkout and recorded with the order.'},
        {type:'returns',title:'Returns',content:'Return eligibility depends on the product and its condition. Unless the product page states otherwise, the customer pays return shipping.'},
        {type:'privacy',title:'Privacy and support',content:'Personal information is used to process orders, provide support and meet legal obligations. Contact Support for a request concerning your order or data.'},
      ],
      de: [{type:'verkauf',title:'Verkaufsbedingungen',content:'Produktspezifikation, Endpreis, Lieferung und Zahlung werden vor Bestellabschluss angezeigt.'},{type:'lieferung',title:'Versand und Lieferung',content:'Lieferziele, voraussichtliche Zeiten und Kosten werden beim Checkout angezeigt.'},{type:'rückgabe',title:'Rückgabe',content:'Die Rückgabemöglichkeit hängt vom Produkt und seinem Zustand ab. Sofern nicht anders angegeben, trägt der Kunde die Rücksendekosten.'},{type:'datenschutz',title:'Datenschutz und Support',content:'Personenbezogene Daten werden zur Bestellabwicklung, Unterstützung und Erfüllung gesetzlicher Pflichten verwendet.'}],
      es: [{type:'venta',title:'Condiciones de venta',content:'Las especificaciones, el precio final, la entrega y el pago se muestran antes de confirmar el pedido.'},{type:'entrega',title:'Envío y entrega',content:'Los destinos, plazos estimados y costes se muestran durante el pago.'},{type:'devolución',title:'Devoluciones',content:'La posibilidad de devolución depende del producto y su estado. Salvo indicación contraria, el cliente paga el envío de devolución.'},{type:'privacidad',title:'Privacidad y atención',content:'Los datos personales se usan para tramitar pedidos, prestar atención y cumplir obligaciones legales.'}],
    };
    return rows[this.i18n.lang()] ?? [];
  });
  protected readonly copy = computed(() => ({
    tr: {
      title: 'Yasal bilgilendirmeler', lead: 'Kullanım, gizlilik ve hizmet koşullarına ait güncel metinleri buradan takip edebilirsin.',
      allPages: 'Tüm yasal sayfalar', openPolicy: 'Bu yasal metni görüntüle.', noPolicies: 'Henüz yasal metin yok',
      noPoliciesLead: 'Yasal metinler yayınlandığında burada görünecek.', updated: 'Son güncelleme',
      notFound: 'Yasal metin bulunamadı', notFoundLead: 'Bu bağlantı için yayınlanmış içerik bulunamadı.'
    },
    en: {
      title: 'Legal information', lead: 'Review the latest terms, privacy and service policies.',
      allPages: 'All legal pages', openPolicy: 'View this legal document.', noPolicies: 'No legal documents yet',
      noPoliciesLead: 'Published legal documents will appear here.', updated: 'Last updated',
      notFound: 'Legal document not found', notFoundLead: 'No published content was found for this link.'
    },
    de: {
      title: 'Rechtliche Informationen', lead: 'Lies die aktuellen Nutzungs-, Datenschutz- und Servicebedingungen.',
      allPages: 'Alle rechtlichen Seiten', openPolicy: 'Dieses Dokument anzeigen.', noPolicies: 'Noch keine rechtlichen Dokumente',
      noPoliciesLead: 'Veröffentlichte Dokumente erscheinen hier.', updated: 'Zuletzt aktualisiert',
      notFound: 'Dokument nicht gefunden', notFoundLead: 'Für diesen Link wurde kein veröffentlichter Inhalt gefunden.'
    },
    es: {
      title: 'Información legal', lead: 'Consulta las condiciones, privacidad y políticas de servicio vigentes.',
      allPages: 'Todas las páginas legales', openPolicy: 'Ver este documento legal.', noPolicies: 'Aún no hay documentos legales',
      noPoliciesLead: 'Los documentos publicados aparecerán aquí.', updated: 'Última actualización',
      notFound: 'Documento no encontrado', notFoundLead: 'No hay contenido publicado para este enlace.'
    },
    fr: {
      title: 'Informations légales', lead: 'Consultez les conditions, la confidentialité et les politiques de service à jour.',
      allPages: 'Toutes les pages légales', openPolicy: 'Voir ce document juridique.', noPolicies: 'Aucun document juridique',
      noPoliciesLead: 'Les documents publiés apparaîtront ici.', updated: 'Dernière mise à jour',
      notFound: 'Document introuvable', notFoundLead: 'Aucun contenu publié ne correspond à ce lien.'
    }
  })[this.i18n.lang()]);

  constructor() {
    this.route.paramMap.subscribe((params) => {
      void this.load(params.get('slug') ?? '');
    });
  }

  private async load(slug: string): Promise<void> {
    this.slug.set(slug);
    this.policies.set([]);
    this.policy.set(null);

    if (!slug) {
      const policies = await this.content.legalPolicies();
      this.policies.set(policies);
      this.seo.setPage({
        title: this.i18n.t('legalSeoTitle'),
        description: this.site.config().brandKey === 'bdsmbeds'
          ? 'Review BDSMBeds sales, delivery, returns, privacy and support information.'
          : this.i18n.t('legalSeoDescription'),
        path: this.i18n.routePath('legal')
      });
      return;
    }

    const policy = await this.content.legalPolicy(slug);
    this.policy.set(policy);
    this.seo.setPage({
      title: policy?.metaTitle || policy?.title || this.i18n.t('legalSeoTitle'),
      description: policy?.metaDescription || policy?.shortDescription || this.i18n.t('legalDetailFallback'),
      path: this.i18n.routePath('legalDetail', { slug })
    });
  }
}
